package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.DraftRepository
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch

enum class StockOrderMode {
    STOCK, ORDERS
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StockAndOrdersScreen(
    initialMode: StockOrderMode = StockOrderMode.STOCK,
    initialPageIndex: Int = 0,
    onPageChange: (Int) -> Unit = {},
    onBack: () -> Unit,
    onNewCount: (Supplier) -> Unit,
    onAmendCount: (Supplier, StockCount) -> Unit,
    onCreateOrder: (Supplier) -> Unit,
    onOrderClick: (Order) -> Unit,
    onShowHistory: (Supplier) -> Unit
) {
    val scope = rememberCoroutineScope()
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val draftRepository = remember { DraftRepository() }

    var suppliers by remember { mutableStateOf<List<Supplier>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentMode by remember { mutableStateOf(initialMode) }
    var hasDraft by remember { mutableStateOf(false) }
    var latestCount by remember { mutableStateOf<StockCount?>(null) }
    var restoredPager by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        repository.getSuppliers().fold(
            onSuccess = {
                suppliers = it
                isLoading = false
            },
            onFailure = {
                error = it.message
                isLoading = false
            }
        )
    }

    val pagerState = rememberPagerState(
        initialPage = initialPageIndex.coerceIn(0, maxOf(0, suppliers.size - 1).coerceAtLeast(0)),
        pageCount = { maxOf(1, suppliers.size) }
    )
    val currentSupplier = suppliers.getOrNull(pagerState.currentPage)

    LaunchedEffect(pagerState.currentPage, suppliers, currentMode) {
        if (suppliers.isNotEmpty()) {
            onPageChange(pagerState.currentPage)
        }
        val supplier = currentSupplier
        if (supplier != null && currentMode == StockOrderMode.STOCK) {
            hasDraft = draftRepository.loadDraft(supplier.id) != null
            latestCount = repository.getStockCounts(supplier.id).getOrNull()?.firstOrNull()
        } else {
            hasDraft = false
            latestCount = null
        }
    }

    LaunchedEffect(suppliers.size) {
        if (!restoredPager && suppliers.isNotEmpty()) {
            pagerState.scrollToPage(initialPageIndex.coerceIn(0, suppliers.lastIndex))
            restoredPager = true
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            topBar = {
                Column {
                    TopAppBar(
                        title = { 
                            Text(if (currentMode == StockOrderMode.STOCK) "Stock Levels" else "Orders") 
                        },
                        navigationIcon = {
                            IconButton(onClick = onBack) {
                                BackArrowIcon()
                            }
                        },
                        actions = {
                            val supplier = currentSupplier
                            if (supplier != null && !isLoading) {
                                ToolbarPlusButton(
                                    onClick = {
                                        if (currentMode == StockOrderMode.STOCK) {
                                            draftRepository.clearDraft(supplier.id)
                                            hasDraft = false
                                            onNewCount(supplier)
                                        } else {
                                            onCreateOrder(supplier)
                                        }
                                    }
                                )
                                if (currentMode == StockOrderMode.STOCK) {
                                    OverflowActionsMenu(
                                        actions = buildList {
                                            if (hasDraft) {
                                                add(OverflowAction("Continue count") { onNewCount(supplier) })
                                            }
                                            latestCount?.let { count ->
                                                add(OverflowAction("Amend last count") { onAmendCount(supplier, count) })
                                            }
                                            add(OverflowAction("History") { onShowHistory(supplier) })
                                        }
                                    )
                                }
                            }
                        }
                    )
                    Surface(
                        modifier = Modifier.fillMaxWidth().height(44.dp),
                        color = MaterialTheme.colorScheme.surfaceVariant
                    ) {
                        Row(modifier = Modifier.fillMaxSize()) {
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxHeight()
                                    .background(if (currentMode == StockOrderMode.STOCK) MaterialTheme.colorScheme.primary else Color.Transparent)
                                    .clickable { currentMode = StockOrderMode.STOCK },
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    "Stock",
                                    color = if (currentMode == StockOrderMode.STOCK) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .fillMaxHeight()
                                    .background(if (currentMode == StockOrderMode.ORDERS) MaterialTheme.colorScheme.secondary else Color.Transparent)
                                    .clickable { currentMode = StockOrderMode.ORDERS },
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    "Orders",
                                    color = if (currentMode == StockOrderMode.ORDERS) MaterialTheme.colorScheme.onSecondary else MaterialTheme.colorScheme.onSurfaceVariant,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    }
                }
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (error != null) {
                    Text("Error: $error", modifier = Modifier.align(Alignment.Center), color = MaterialTheme.colorScheme.error)
                } else if (!isLoading && suppliers.isEmpty()) {
                    Text("No suppliers found.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    Column(modifier = Modifier.fillMaxSize()) {
                        ScrollableTabRow(
                            selectedTabIndex = pagerState.currentPage.coerceIn(0, maxOf(0, suppliers.size - 1)),
                            edgePadding = 16.dp,
                            containerColor = MaterialTheme.colorScheme.surface,
                            contentColor = MaterialTheme.colorScheme.primary,
                            divider = {},
                            indicator = { tabPositions ->
                                val index = pagerState.currentPage.coerceIn(0, maxOf(0, tabPositions.lastIndex))
                                if (tabPositions.isNotEmpty()) {
                                    TabRowDefaults.SecondaryIndicator(
                                        modifier = Modifier.tabIndicatorOffset(tabPositions[index]),
                                        color = if (currentMode == StockOrderMode.STOCK) MaterialTheme.colorScheme.secondary else MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                        ) {
                            suppliers.forEachIndexed { index, supplier ->
                                Tab(
                                    selected = pagerState.currentPage == index,
                                    onClick = { scope.launch { pagerState.animateScrollToPage(index) } },
                                    text = { Text(supplier.name) }
                                )
                            }
                        }

                        HorizontalPager(
                            state = pagerState,
                            modifier = Modifier.fillMaxWidth().weight(1f)
                        ) { page ->
                            val supplier = suppliers.getOrNull(page)
                            if (supplier != null) {
                                if (currentMode == StockOrderMode.STOCK) {
                                    SupplierStockView(
                                        supplier = supplier,
                                        repository = repository
                                    )
                                } else {
                                    SupplierOrdersView(
                                        supplier = supplier,
                                        repository = repository,
                                        onOrderClick = onOrderClick,
                                        onCreateOrder = { onCreateOrder(supplier) },
                                        showCreateButton = false
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { /* Block touches */ },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }
}
